import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePVData } from '../context/PVDataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import InverterCheckForm from './InverterCheckForm';
import InverterHistory from './InverterHistory';
import InverterQuickCheck from './InverterQuickCheck';
import supabase from '../lib/supabase';

const { FiPlus, FiSearch, FiCalendar, FiCheck, FiX, FiAlertTriangle, FiFileText, FiFilter, FiList, FiClock } = FiIcons;

const InverterChecks = () => {
  const { plants } = usePVData();
  const [inverterChecks, setInverterChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showQuickCheck, setShowQuickCheck] = useState(false);
  const [editingCheck, setEditingCheck] = useState(null);
  const [selectedInverter, setSelectedInverter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlant, setSelectedPlant] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedCheck, setExpandedCheck] = useState(null);
  const [groupedByPlant, setGroupedByPlant] = useState({});

  // Lade Wechselrichterprüfungen aus Supabase
  useEffect(() => {
    fetchInverterChecks();
  }, []);

  // Filter und Suche anwenden
  const filteredChecks = inverterChecks.filter(check => {
    // Suche
    const searchMatch = check.inverter_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (check.inverter_bmr_name && check.inverter_bmr_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      check.serial_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      check.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      check.check_result.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (check.plants?.name && check.plants.name.toLowerCase().includes(searchTerm.toLowerCase()));

    // Anlagenfilter
    const plantMatch = selectedPlant === 'all' || check.plant_id === selectedPlant;

    // Statusfilter
    const statusMatch = filterStatus === 'all' || 
      (filterStatus === 'passed' && check.check_result.toLowerCase() === 'bestanden') ||
      (filterStatus === 'failed' && check.check_result.toLowerCase() === 'nicht bestanden');

    return searchMatch && plantMatch && statusMatch;
  });

  // Gruppiere nach Anlagen für eine übersichtlichere Darstellung
  useEffect(() => {
    const grouped = {};
    filteredChecks.forEach(check => {
      const plantId = check.plant_id;
      const plantName = check.plants?.name || 'Unbekannte Anlage';
      
      if (!grouped[plantId]) {
        grouped[plantId] = {
          plantName,
          inverters: []
        };
      }
      
      grouped[plantId].inverters.push(check);
    });
    setGroupedByPlant(grouped);
  }, [filteredChecks]);

  const handleAddCheck = () => {
    setEditingCheck(null);
    setShowForm(true);
  };

  const handleEditCheck = (check) => {
    setEditingCheck(check);
    setShowForm(true);
  };

  const handleCloseForm = (refreshData = false) => {
    setShowForm(false);
    setEditingCheck(null);
    if (refreshData) {
      // Daten neu laden
      fetchInverterChecks();
    }
  };

  const handleViewHistory = (inverter) => {
    setSelectedInverter(inverter);
    setShowHistory(true);
  };

  const handleQuickCheck = (inverter) => {
    setSelectedInverter(inverter);
    setShowQuickCheck(true);
  };

  const handleToggleDetails = (checkId) => {
    if (expandedCheck === checkId) {
      setExpandedCheck(null);
    } else {
      setExpandedCheck(checkId);
    }
  };

  const fetchInverterChecks = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('pv_inverter_checks_a7b3c9d8e2')
        .select(`
          *,
          plants:plant_id (
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching inverter checks:', error);
        return;
      }

      setInverterChecks(data || []);
    } catch (error) {
      console.error('Exception fetching inverter checks:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteInverterCheck = async (checkId) => {
    if (!window.confirm('Möchten Sie diesen Wechselrichter wirklich löschen? Dies löscht auch alle Prüfungsdaten!')) return;

    try {
      const { error } = await supabase
        .from('pv_inverter_checks_a7b3c9d8e2')
        .delete()
        .eq('id', checkId);

      if (error) {
        console.error('Error deleting inverter check:', error);
        return;
      }

      // Aktualisiere die Liste nach dem Löschen
      setInverterChecks(inverterChecks.filter(check => check.id !== checkId));
    } catch (error) {
      console.error('Exception deleting inverter check:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Wechselrichterprüfungen</h1>
          <p className="text-gray-600 mt-1">Wöchentliche Prüfung jeden Donnerstag</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAddCheck}
          className="bg-primary-500 text-white px-4 py-2 md:px-6 md:py-3 rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center space-x-2 shadow-lg"
        >
          <SafeIcon icon={FiPlus} />
          <span>Neuer Wechselrichter</span>
        </motion.button>
      </motion.div>

      {/* Filter und Suchleiste */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
          />
        </div>

        <div className="relative">
          <SafeIcon icon={FiFileText} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <select
            value={selectedPlant}
            onChange={(e) => setSelectedPlant(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white appearance-none"
          >
            <option value="all">Alle Anlagen</option>
            {plants.map(plant => (
              <option key={plant.id} value={plant.id}>{plant.name}</option>
            ))}
          </select>
          <SafeIcon icon={FiFilter} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>

        <div className="relative">
          <SafeIcon icon={FiCheck} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white appearance-none"
          >
            <option value="all">Alle Ergebnisse</option>
            <option value="passed">Bestanden</option>
            <option value="failed">Nicht bestanden</option>
          </select>
          <SafeIcon icon={FiFilter} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </motion.div>

      {/* Wechselrichterliste, gruppiert nach Anlagen */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {Object.keys(groupedByPlant).length > 0 ? (
            Object.entries(groupedByPlant).map(([plantId, plantData], plantIndex) => (
              <motion.div
                key={plantId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: plantIndex * 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                <div className="bg-gray-50 p-4 md:p-6 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900">{plantData.plantName}</h3>
                  <p className="text-sm text-gray-600 mt-1">{plantData.inverters.length} Wechselrichter</p>
                </div>
                
                <div className="p-4 md:p-6 space-y-4">
                  {plantData.inverters.map((inverter, index) => (
                    <motion.div
                      key={inverter.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      <div className="p-4 cursor-pointer" onClick={() => handleToggleDetails(inverter.id)}>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-start space-x-4">
                            <div className={`p-3 rounded-lg ${
                              inverter.check_result.toLowerCase() === 'bestanden' 
                                ? 'bg-green-100 text-green-600' 
                                : 'bg-red-100 text-red-600'
                            }`}>
                              <SafeIcon 
                                icon={inverter.check_result.toLowerCase() === 'bestanden' ? FiCheck : FiAlertTriangle} 
                                className="text-xl" 
                              />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{inverter.inverter_name}</h3>
                              <div className="text-sm text-gray-500 flex flex-wrap gap-x-4 gap-y-2 mt-1">
                                <div className="flex items-center">
                                  <SafeIcon icon={FiFileText} className="mr-1" />
                                  SN: {inverter.serial_number}
                                </div>
                                <div className="flex items-center">
                                  <SafeIcon icon={FiCalendar} className="mr-1" />
                                  {formatDate(inverter.check_date)}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 md:space-x-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              inverter.check_result.toLowerCase() === 'bestanden' 
                                ? 'bg-green-100 text-green-600' 
                                : 'bg-red-100 text-red-600'
                            }`}>
                              {inverter.check_result}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleQuickCheck(inverter);
                              }}
                              className="px-3 py-1 bg-primary-100 text-primary-600 rounded-full text-xs font-medium hover:bg-primary-200 transition-colors"
                            >
                              Prüfen
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Erweiterte Details */}
                      <AnimatePresence>
                        {expandedCheck === inverter.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="border-t border-gray-100"
                          >
                            <div className="p-4 md:p-6 bg-gray-50">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                  <p className="text-sm text-gray-500">Wechselrichter BMR Name</p>
                                  <p className="font-medium">{inverter.inverter_bmr_name || 'Nicht angegeben'}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Seriennummer</p>
                                  <p className="font-medium">{inverter.serial_number}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Standort</p>
                                  <p className="font-medium">{inverter.location}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Letzte Prüfung</p>
                                  <p className="font-medium">{formatDate(inverter.check_date)}</p>
                                </div>
                              </div>
                              
                              {inverter.photo_url && (
                                <div className="mb-4">
                                  <p className="text-sm text-gray-500 mb-2">Prüfungsfoto</p>
                                  <img 
                                    src={inverter.photo_url} 
                                    alt="Prüfungsfoto" 
                                    className="w-full max-w-md rounded-lg shadow-sm" 
                                  />
                                </div>
                              )}
                              
                              <div className="flex flex-wrap justify-end space-x-2 md:space-x-3 mt-4">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewHistory(inverter);
                                  }}
                                  className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center space-x-1"
                                >
                                  <SafeIcon icon={FiClock} />
                                  <span>Prüfverlauf</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickCheck(inverter);
                                  }}
                                  className="px-4 py-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors flex items-center space-x-1"
                                >
                                  <SafeIcon icon={FiCheck} />
                                  <span>Prüfen</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditCheck(inverter);
                                  }}
                                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-1"
                                >
                                  <SafeIcon icon={FiIcons.FiEdit} />
                                  <span>Bearbeiten</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteInverterCheck(inverter.id);
                                  }}
                                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-1"
                                >
                                  <SafeIcon icon={FiIcons.FiTrash2} />
                                  <span>Löschen</span>
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-8 md:py-12"
            >
              <div className="text-gray-400 text-4xl md:text-6xl mb-4">
                <SafeIcon icon={FiSearch} className="mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchTerm || selectedPlant !== 'all' || filterStatus !== 'all'
                  ? 'Keine Wechselrichter gefunden'
                  : 'Keine Wechselrichter vorhanden'}
              </h3>
              <p className="text-gray-600">
                {searchTerm || selectedPlant !== 'all' || filterStatus !== 'all'
                  ? 'Versuchen Sie andere Suchkriterien'
                  : 'Fügen Sie Ihren ersten Wechselrichter hinzu'}
              </p>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Form Modals */}
      <AnimatePresence>
        {showForm && (
          <InverterCheckForm check={editingCheck} onClose={handleCloseForm} plants={plants} />
        )}
        
        {showHistory && selectedInverter && (
          <InverterHistory 
            inverter={selectedInverter} 
            onClose={() => setShowHistory(false)} 
            onRefresh={fetchInverterChecks}
          />
        )}
        
        {showQuickCheck && selectedInverter && (
          <InverterQuickCheck 
            inverter={selectedInverter} 
            onClose={() => {
              setShowQuickCheck(false);
              fetchInverterChecks();
            }} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default InverterChecks;